import { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiPathsType } from 'services/api/apiPaths';
import RedisApiAxiosInstance from './RedisApiAxiosInstance/RedisApiAxiosInstance';

type ConfigWithData = {
  path?: string;
  config?: AxiosRequestConfig;
  data?: unknown;
};

type ConfigWithoutData = {
  path?: string;
  config?: AxiosRequestConfig;
};

const createApiPath = (baseResourcePath: string, path?: string) =>
  path ? `${baseResourcePath}/${path}` : baseResourcePath;

/**
 * Creates a typed REST API client for a resource.
 *
 * Common methods: getAll, getOne, create, update, remove
 *
 * @example
 * ```typescript
 * export const usersApi = createResourceApi(ApiPaths.USERS, (rest) => ({
 *   getAll: () => rest.get<{ users: User[] }>(),
 *   getOne: (id: number) => rest.get<{ user: User }>({ path: `${id}` }),
 *   create: (user: Partial<User>) => rest.post<{ user: User }>({ data: { user } }),
 *   update: (user: User) => rest.put<{ user: User }>({ path: `${user.id}`, data: { user } }),
 *   remove: (id: number) => rest.delete({ path: `${id}` })
 * }));
 * ```
 */
export const createResourceApi = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, (...args: any[]) => Promise<AxiosResponse<any>>>
>(
  baseResourcePath: ApiPathsType,
  resource: (
    props: {
      get: <R>(props?: ConfigWithoutData) => Promise<AxiosResponse<R>>;
      post: <R>(props?: ConfigWithData) => Promise<AxiosResponse<R>>;
      put: <R>(props?: ConfigWithData) => Promise<AxiosResponse<R>>;
      delete: <R>(props?: ConfigWithoutData) => Promise<AxiosResponse<R>>;
    },
    instance: AxiosInstance
  ) => T
): T =>
  resource(
    {
      get: (props) =>
        RedisApiAxiosInstance.get(createApiPath(baseResourcePath, props?.path), props?.config),
      post: (props) =>
        RedisApiAxiosInstance.post(
          createApiPath(baseResourcePath, props?.path),
          props?.data,
          props?.config
        ),
      put: (props) =>
        RedisApiAxiosInstance.put(
          createApiPath(baseResourcePath, props?.path),
          props?.data,
          props?.config
        ),
      delete: (props) =>
        RedisApiAxiosInstance.delete(createApiPath(baseResourcePath, props?.path), props?.config)
    },
    RedisApiAxiosInstance
  );
